/* eslint-disable max-lines -- Pre-existing violation */
import {isFeatureEnabled} from 'feature-flags';
import {LabelActions, showNotification} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {type KeyboardEventHandler, useRef, useState} from 'react';

import {equals, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useComposeMessageMutation} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {
  COMPOSE_MESSAGE_PLACEHOLDER_MISMATCH_CODE,
  COMPOSE_MESSAGE_RECORD_CONTEXT_UNAVAILABLE_CODE,
  COMPOSE_MESSAGE_UPSTREAM_TIMEOUT_CODE,
  type ComposeMessageContext,
  ComposeMessageRequest,
  errorResponseSchema,
  COMPOSE_MESSAGE_BODY_MAX_LENGTH,
} from '@prompt-bridge/shared/validations';

import {
  ApiError,
  isFetchBaseQueryError,
  Nullish,
  parseDate,
  useLatest,
  useTimeout,
} from 'shared/web';

import {isEmailHtmlEmpty} from '../../utils/isEmailHtmlEmpty';
import {
  AiComposerApplyMode,
  AiComposerAppliedStep,
  AiComposerBodyFormat,
  AiComposerChannel,
  AiComposerDraft,
  AiComposerEditor,
  AiComposerIdentity,
  AiComposerInstructionSource,
  AiComposerPlaceholder,
  AiComposerProposal,
  AiComposerProvenance,
  AiComposerRecord,
  AiComposerRefinement,
  AiComposerSuggestion,
  AiComposerUndoStep,
} from './AiComposerDraft';
import {buildAppliedDraft} from './buildAppliedDraft';
import {canAutoApplyGeneratedDraft} from './canAutoApplyGeneratedDraft';
import {comparePlaceholderLiterals} from './comparePlaceholderLiterals';
import {formatAiComposerFactValue} from './formatAiComposerFactValue';
import {getAiComposerSuggestions} from './getAiComposerSuggestions';
import {isPlaceholderSafeDraft} from './isPlaceholderSafeDraft';
import {prepareProposedBody} from './prepareProposedBody';
import {useAiComposerRecordContext} from './useAiComposerRecordContext';

const HTML_BODY_FORMAT: AiComposerBodyFormat = 'HTML';
const FORBIDDEN_STATUS = 403;
const NOT_FOUND_STATUS = 404;
const GATEWAY_TIMEOUT_STATUS = 504;
/** Enough to step back through a working session; older steps fall off the bottom. */
const MAX_UNDO_STEPS = 20;

export type AiComposerErrorKind =
  | 'placeholderMismatch'
  | 'forbidden'
  | 'recordUnavailable'
  | 'network'
  | 'timeout'
  | 'unavailable';

const CHANGE_HIGHLIGHT_MS = 6000;
const EMPTY_DRAFT: AiComposerDraft = {body: '', subject: ''};
/** Facts "Add detail" may not fall back on: identifiers, states and the addressee are not detail. */
const NON_DETAIL_FACT =
  /(\.number|\.vin|\.registrationPlate|\.state)$|^(recipient|customer)\.name$|^replyTo\./;

const toPlainText = (body: string, isHtml: boolean) =>
  isHtml ? (new DOMParser().parseFromString(body, 'text/html').body.textContent ?? '') : body;

const WHITESPACE = /[\s\u00a0\u202f]/g;
const CURRENCY_CODE = /^[A-Z]{3}$/;

/**
 * The local fact keeps the database form ("100000.00 CZK", "154875"); the message carries the
 * customer form ("100 000 Kč", "154 875 km"). A fact counts as used when its digits are in the text.
 */
const isFactInText = (value: string, text: string) => {
  if (text.includes(value)) {
    return true;
  }
  const [amount, currency, ...rest] = value.split(' ');
  const digits =
    isNotNil(amount) && rest.length === 0 && (isNil(currency) || CURRENCY_CODE.test(currency))
      ? amount.split('.')[0]
      : null;

  return (
    isNotNil(digits) && /^-?\d{2,}$/.test(digits) && text.replace(WHITESPACE, '').includes(digits)
  );
};

/**
 * A generated message replaces the field, so its placeholders are judged on their own: each must
 * come from the catalogue, once. A refinement, and anything appended below the current text, is
 * judged against the text it keeps. The server applies the same two rules.
 */
const placeholderBaseline = (
  proposal: AiComposerProposal,
  mode: AiComposerApplyMode,
  current: AiComposerDraft
): AiComposerDraft =>
  proposal.operation === 'generate' && mode === 'replace' ? EMPTY_DRAFT : current;

const buildIdentity = (
  record: AiComposerRecord | Nullish,
  draft: AiComposerDraft
): AiComposerIdentity => ({
  context: record?.context ?? '',
  recordId: record?.recordId ?? '',
  customerId: record?.customerId ?? '',
  recipientAddress: record?.recipientAddress ?? '',
  replyToMessageId: record?.replyTo?.messageId ?? '',
  body: draft.body,
  subject: draft.subject ?? '',
});

const toComparableDraft = (draft: AiComposerDraft): AiComposerDraft => ({
  body: draft.body,
  subject: draft.subject ?? '',
});

const toErrorKind = (error: ApiError): AiComposerErrorKind => {
  const fetchError = isFetchBaseQueryError(error) ? error : null;
  // A request that never reached the server ({status: 'FETCH_ERROR'}) carries no `data`, so the
  // guard above does not see it; its status is read on its own.
  const status = isNotNil(error) && 'status' in error ? error.status : null;
  const response = errorResponseSchema.safeParse(fetchError?.data);

  return match({status, code: response.success ? response.data.code : null})
    .with({code: COMPOSE_MESSAGE_PLACEHOLDER_MISMATCH_CODE}, () => 'placeholderMismatch' as const)
    .with(
      {code: COMPOSE_MESSAGE_RECORD_CONTEXT_UNAVAILABLE_CODE},
      () => 'recordUnavailable' as const
    )
    .with({code: COMPOSE_MESSAGE_UPSTREAM_TIMEOUT_CODE}, () => 'timeout' as const)
    .with({status: 'FETCH_ERROR'}, () => 'network' as const)
    .with({status: 'TIMEOUT_ERROR'}, () => 'timeout' as const)
    .with({status: GATEWAY_TIMEOUT_STATUS}, () => 'timeout' as const)
    .with({status: FORBIDDEN_STATUS}, () => 'forbidden' as const)
    .with({status: NOT_FOUND_STATUS}, () => 'recordUnavailable' as const)
    .otherwise(() => 'unavailable' as const);
};

export interface UseAiMessageComposerParams {
  channel: AiComposerChannel;
  bodyFormat: AiComposerBodyFormat;
  draft: AiComposerDraft;
  /** Writes an accepted draft. An omitted subject preserves it; an empty string clears it. */
  onApply: (draft: AiComposerDraft) => void;
  record?: AiComposerRecord | Nullish;
  editor?: AiComposerEditor;
  locale?: string | Nullish;
  /** The variables a template may use. Enables generation without a record and lets a proposal introduce them. */
  placeholders?: AiComposerPlaceholder[] | Nullish;
  isDisabled?: boolean;
  isInitiallyOpen?: boolean;
}

export interface AiMessageComposerController {
  channel: AiComposerChannel;
  onEditorKeyDownCapture: KeyboardEventHandler<HTMLDivElement>;
  /** `false` when `AI_EMAIL_GENERATOR` is off: no entry point, no panel, manual editing untouched. */
  isEnabled: boolean;
  isOpen: boolean;
  isPromptOpen: boolean;
  hasBarContent: boolean;
  isPending: boolean;
  isError: boolean;
  errorKind: AiComposerErrorKind | null;
  isChangeHighlighted: boolean;
  isProposalWithdrawn: boolean;
  canGenerate: boolean;
  /** `true` when a generate request would pass validation: an instruction is required. */
  canRunGenerate: boolean;
  canUndo: boolean;
  /** `false` when replacing would change a template placeholder. */
  canApplyReplace: boolean;
  /**
   * `false` for a refinement, which rewrites the text it would sit below, and when appending
   * would repeat a template placeholder the original already has.
   */
  canApplyInsertBelow: boolean;
  /** `true` when "Insert below" is offered at all: a generated draft for a field with text. */
  isInsertBelowOffered: boolean;
  /** `true` when accepting the proposal writes its subject; a subject the employee owns stays. */
  isProposedSubjectApplied: boolean;
  /** `false` when the record offers no detail the message does not already carry; the button is then hidden. */
  canRefineDetail: boolean;
  canRefineSelection: boolean;
  hasExistingBody: boolean;
  hasSubject: boolean;
  isHtmlBody: boolean;
  entryTitle: string;
  isEntryDisabled: boolean;
  instruction: string;
  selectionInstruction: string;
  suggestions: AiComposerSuggestion[];
  recordContext: ComposeMessageContext | null;
  isRecordContextLoading: boolean;
  isRecordContextError: boolean;
  proposal: AiComposerProposal | null;
  undoStep: AiComposerUndoStep | null;
  activeSources: 'proposal' | 'applied' | null;
  onSourcesChange: (sources: AiMessageComposerController['activeSources']) => void;
  appliedProvenance: AiComposerProvenance | null;
  labelAction: LabelActions[number] | null;
  onOpen: () => void;
  onClose: () => void;
  /** Dismisses the prompt popover; closes the composer when nothing is left in the footer bar. */
  onPromptClose: () => void;
  onInstructionChange: (instruction: string | null) => void;
  onSelectionInstructionChange: (instruction: string | null) => void;
  onGenerate: () => void;
  onGenerateSuggestion: (instruction: string) => void;
  /** A quick action on the text alone; the instruction field is not part of it. */
  onRefine: (refinement: AiComposerRefinement) => void;
  /** A refinement steered by the instruction the employee typed into the panel. */
  onRefineWithInstruction: (refinement: AiComposerRefinement) => void;
  onRefineSelection: () => void;
  onStop: () => void;
  onApply: (mode: AiComposerApplyMode) => void;
  onDiscard: () => void;
  onUndo: () => void;
  /** Escape anywhere in the composer: cancels the current step, never the surrounding form. */
  onEscape: () => void;
}

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function useAiMessageComposer(
  params: UseAiMessageComposerParams
): AiMessageComposerController {
  const isEnabled = isFeatureEnabled(featureFlags.AI_EMAIL_GENERATOR);
  // A flag that turns off while a request is in flight must also stop its answer from landing.
  const isEnabledRef = useLatest(isEnabled);

  const [isOpen, setIsOpen] = useState(params.isInitiallyOpen ?? false);
  const [isPromptOpen, setIsPromptOpen] = useState(params.isInitiallyOpen ?? false);
  const [instruction, setInstruction] = useState('');
  const [selectionInstruction, setSelectionInstruction] = useState('');
  const [proposal, setProposal] = useState<AiComposerProposal | null>(null);
  const [appliedSteps, setAppliedSteps] = useState<AiComposerAppliedStep[]>([]);
  const [activeSources, setActiveSources] =
    useState<AiMessageComposerController['activeSources']>(null);
  const [errorKind, setErrorKind] = useState<AiComposerErrorKind | null>(null);
  const isError = isNotNil(errorKind);
  const [isChangeHighlighted, setIsChangeHighlighted] = useState(false);
  const [isEditorFocusPending, setIsEditorFocusPending] = useState(false);
  // Offers are judged against the moment the prompt opened, not against every render.
  const [openedAt, setOpenedAt] = useState(() => Date.now());

  const [composeMessage, {isLoading}] = useComposeMessageMutation();
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();

  const draftRef = useLatest(params.draft);
  const recordRef = useLatest(params.record);
  const editorRef = useLatest(params.editor);
  const onApplyRef = useLatest(params.onApply);

  const requestIdRef = useRef(0);
  const pendingRequestRef = useRef<ReturnType<typeof composeMessage> | null>(null);
  const focusRequestEditorRef = useRef<AiComposerEditor>(null);

  const isHtml = params.bodyFormat === HTML_BODY_FORMAT;
  const hasExistingBody = isHtml
    ? !isEmailHtmlEmpty(params.draft.body)
    : params.draft.body.trim().length > 0;
  const placeholders = params.placeholders ?? [];
  const hasPlaceholderCatalogue = placeholders.length > 0;
  const allowedLiterals = placeholders.map((placeholder) => placeholder.literal);
  const canGenerate =
    (params.channel === 'email' &&
      isNotNil(params.record?.context) &&
      isNotNil(params.record?.recordId)) ||
    hasPlaceholderCatalogue;

  // Local reads select suggestions only. Node still fetches and authorizes generation facts.
  const recordContext = useAiComposerRecordContext({
    record: params.record,
    isEnabled: isEnabled && isOpen && canGenerate,
  });

  // "Add detail" only when there is a detail to add. A record whose remaining facts are the case
  // number and the VIN would make the model pad the message with identifiers (seen in review).
  const bodyText = toPlainText(params.draft.body, isHtml);
  const canRefineDetail =
    hasExistingBody &&
    (hasPlaceholderCatalogue ||
      (recordContext.context?.facts ?? []).some(
        (fact) => !NON_DETAIL_FACT.test(fact.key) && !isFactInText(fact.value, bodyText)
      ));

  const currentIdentity = buildIdentity(params.record, params.draft);
  // The whole identity, not just the record: a hand edit while a proposal is open makes that
  // proposal a rewrite of text that is no longer there, and its selection range meaningless.
  const isProposalWithdrawn = isNotNil(proposal) && !equals(proposal.identity, currentIdentity);
  const activeProposal = isProposalWithdrawn ? null : proposal;

  // A different record or recipient must not inherit the previous accepted step.
  const appliedStep = appliedSteps.at(-1) ?? null;
  const isAppliedIdentityIntact =
    isNotNil(appliedStep) &&
    equals(appliedStep.provenance.identity, buildIdentity(params.record, appliedStep.applied));
  const isAppliedDraftIntact =
    isAppliedIdentityIntact &&
    isNotNil(appliedStep) &&
    equals(appliedStep.applied, toComparableDraft(params.draft));
  const canUndo = isAppliedDraftIntact;

  // The employee owns a subject they typed or the form filled in; only an empty one, or the one
  // the assistant wrote and nobody changed since, may be replaced (seen in review: a typed
  // subject disappeared on Replace).
  const canReplaceSubject = (draft: AiComposerDraft) =>
    (draft.subject ?? '').trim() === '' ||
    (isAppliedIdentityIntact &&
      isNotNil(appliedStep) &&
      appliedStep.hasAiSubject &&
      (draft.subject ?? '') === (appliedStep.applied.subject ?? ''));
  const hasBarContent =
    isLoading || isNotNil(activeProposal) || isAppliedIdentityIntact || isProposalWithdrawn;

  useTimeout(() => setIsChangeHighlighted(false), isChangeHighlighted ? CHANGE_HIGHLIGHT_MS : null);
  useTimeout(
    () => {
      const editor = focusRequestEditorRef.current;

      if (editor === editorRef.current) {
        editor?.focus();
      }

      focusRequestEditorRef.current = null;
      setIsEditorFocusPending(false);
    },
    isEditorFocusPending ? 0 : null
  );

  const preparedProposalBody = isNotNil(activeProposal)
    ? prepareProposedBody(activeProposal.draft.body, isHtml, isNotNil(activeProposal.selection))
    : '';
  const comparableDraft = toComparableDraft(params.draft);
  const isSubjectReplaceable = canReplaceSubject(comparableDraft);
  const isInsertBelowOffered =
    hasExistingBody &&
    isNotNil(activeProposal) &&
    isNil(activeProposal.selection) &&
    activeProposal.operation === 'generate';

  const canApplyReplace =
    isNotNil(activeProposal) &&
    (isNotNil(activeProposal.selection)
      ? !activeProposal.isPlaceholderMismatch
      : isPlaceholderSafeDraft(
          placeholderBaseline(activeProposal, 'replace', comparableDraft),
          buildAppliedDraft(
            comparableDraft,
            preparedProposalBody,
            activeProposal.draft.subject,
            'replace',
            isHtml,
            isSubjectReplaceable
          ),
          allowedLiterals
        ));
  const canApplyInsertBelow =
    isInsertBelowOffered &&
    isNotNil(activeProposal) &&
    isPlaceholderSafeDraft(
      comparableDraft,
      buildAppliedDraft(
        comparableDraft,
        preparedProposalBody,
        activeProposal.draft.subject,
        'insertBelow',
        isHtml,
        isSubjectReplaceable
      ),
      allowedLiterals
    );
  const isProposedSubjectApplied =
    isNotNil(activeProposal) &&
    isNil(activeProposal.selection) &&
    isNotNil(activeProposal.draft.subject) &&
    isSubjectReplaceable &&
    activeProposal.draft.subject !== comparableDraft.subject;

  const abortPendingRequest = () => {
    // Bumping the id first makes the rejection that follows the abort unattributable to any
    // request still in flight, so it can neither raise an error nor commit a result.
    requestIdRef.current = requestIdRef.current + 1;
    pendingRequestRef.current?.abort();
    pendingRequestRef.current = null;
  };

  const runRequest = (
    operation: ComposeMessageRequest['operation'],
    refinement?: AiComposerRefinement,
    instructionOverride?: string,
    instructionSource: AiComposerInstructionSource = 'panel'
  ) => {
    if (!isEnabledRef.current) {
      return;
    }

    const record = recordRef.current;
    const draft = draftRef.current;
    const identity = buildIdentity(record, draft);
    const editor = editorRef.current;
    const selection = refinement === 'selection' ? editor?.getSelection() : null;
    const requestInstruction = instructionOverride ?? instruction;

    if (refinement === 'selection' && isNil(selection)) {
      showNotification.warning(i18n.t('general.aiMessageComposer.notifications.selectTextFirst'));
      return;
    }

    const selectionContent = isNotNil(selection) ? (editor?.getContent(selection) ?? '') : null;
    // The whole message travels with a selection rewrite, so the model can make the rewritten
    // words connect to their neighbours. A draft longer than the server accepts falls back to the
    // fragment alone: the older single-fragment contract, which the server still honours.
    const isBodyWithinLimit = draft.body.length <= COMPOSE_MESSAGE_BODY_MAX_LENGTH;
    const requestBody =
      isNotNil(selectionContent) && !isBodyWithinLimit ? selectionContent : draft.body;
    const requestSelection =
      isNotNil(selectionContent) && isBodyWithinLimit ? selectionContent : undefined;
    const requestSubject =
      isNil(selection) && params.channel === 'email' && !isNilOrEmpty(draft.subject)
        ? draft.subject
        : undefined;

    abortPendingRequest();
    const requestId = requestIdRef.current;

    setErrorKind(null);
    setIsChangeHighlighted(false);
    setProposal(null);
    setActiveSources(null);
    setIsPromptOpen(false);
    focusRequestEditorRef.current = editor;
    setIsEditorFocusPending(true);

    const source =
      isNotNil(record?.context) && isNotNil(record?.recordId)
        ? {
            context: record.context,
            recordId: record.recordId,
            customerId: record.customerId ?? undefined,
            replyToMessageId: record.replyTo?.messageId ?? undefined,
          }
        : undefined;

    const request: ComposeMessageRequest = {
      operation,
      channel: params.channel,
      instruction: requestInstruction,
      body: requestBody,
      subject: requestSubject,
      bodyFormat: params.bodyFormat,
      locale: params.locale ?? undefined,
      source,
      refinement,
      selection: requestSelection,
      placeholders: hasPlaceholderCatalogue ? placeholders : undefined,
    };

    const pending = composeMessage(request);
    pendingRequestRef.current = pending;

    pending
      .unwrap()
      .then((response) => {
        if (requestId !== requestIdRef.current || !isEnabledRef.current) {
          return;
        }

        const proposedSubject = response.subject;
        const baseline =
          operation === 'generate'
            ? EMPTY_DRAFT
            : {body: selectionContent ?? requestBody, subject: requestSubject ?? ''};
        const isSubjectPreserved =
          isNil(proposedSubject) ||
          comparePlaceholderLiterals(baseline.subject ?? '', proposedSubject, allowedLiterals);

        const nextProposal: AiComposerProposal = {
          operation,
          draft: {body: response.body, subject: proposedSubject},
          usedContext: response.usedContext,
          unavailableContext: response.unavailableContext,
          languageSource: response.languageSource,
          locale: response.locale,
          identity,
          instructionSource,
          selection,
          isPlaceholderMismatch:
            !comparePlaceholderLiterals(baseline.body, response.body, allowedLiterals) ||
            !isSubjectPreserved,
        };

        if (
          canAutoApplyGeneratedDraft(operation, draft, isHtml, isNotNil(selection)) &&
          equals(identity, buildIdentity(recordRef.current, draftRef.current))
        ) {
          const applied = buildAppliedDraft(
            draft,
            prepareProposedBody(response.body, isHtml),
            proposedSubject,
            'replace',
            isHtml,
            canReplaceSubject(draft)
          );

          if (
            !nextProposal.isPlaceholderMismatch &&
            isPlaceholderSafeDraft(draft, applied, allowedLiterals)
          ) {
            commitApplied(nextProposal, toComparableDraft(draft), applied, false);
            return;
          }
        }

        setProposal(nextProposal);
      })
      .catch((error: ApiError) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setErrorKind(toErrorKind(error));
        setIsPromptOpen(true);
      });
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsPromptOpen(true);
    setOpenedAt(Date.now());
    setErrorKind(null);
  };

  const handleClose = () => {
    abortPendingRequest();
    setIsOpen(false);
    setIsPromptOpen(false);
    setProposal(null);
    setActiveSources(null);
    setErrorKind(null);
    focusRequestEditorRef.current = null;
    setIsEditorFocusPending(false);
    // The instruction survives a dismissal: a click beside the popover must not throw away what
    // the employee has typed. An accepted draft clears it.
    editorRef.current?.focus();
  };

  const commitApplied = (
    committedProposal: AiComposerProposal,
    previous: AiComposerDraft,
    applied: AiComposerDraft,
    shouldFocusEditor: boolean
  ) => {
    onApplyRef.current(applied);
    const appliedSubject = applied.subject ?? '';
    const step: AiComposerAppliedStep = {
      previous,
      applied: toComparableDraft(applied),
      provenance: {
        identity: {
          ...committedProposal.identity,
          body: applied.body,
          subject: appliedSubject,
        },
        usedContext: committedProposal.usedContext,
        unavailableContext: committedProposal.unavailableContext,
        languageSource: committedProposal.languageSource,
        locale: committedProposal.locale,
      },
      // A subject the assistant wrote stays its own until someone edits it.
      hasAiSubject:
        appliedSubject !== (previous.subject ?? '')
          ? appliedSubject.trim() !== ''
          : appliedSubject.trim() !== '' && canReplaceSubject(previous),
    };
    setAppliedSteps((steps) => [...steps, step].slice(-MAX_UNDO_STEPS));
    setProposal(null);
    if (committedProposal.instructionSource === 'selectionToolbar') {
      setSelectionInstruction('');
    } else {
      setInstruction('');
    }
    setActiveSources(null);
    setIsChangeHighlighted(true);
    if (shouldFocusEditor) {
      editorRef.current?.focus();
    }
  };

  const handleApply = (mode: AiComposerApplyMode) => {
    if (isNil(activeProposal) || !isEnabled) {
      return;
    }

    const previous = toComparableDraft(draftRef.current);

    if (!equals(activeProposal.identity, buildIdentity(recordRef.current, draftRef.current))) {
      return;
    }

    const editor = editorRef.current;
    const selection = activeProposal.selection;
    const preparedBody = prepareProposedBody(
      activeProposal.draft.body,
      isHtml,
      isNotNil(selection)
    );

    if (isNotNil(selection)) {
      if (isNil(editor) || activeProposal.isPlaceholderMismatch) {
        return;
      }

      commitApplied(
        activeProposal,
        previous,
        {
          body: editor.replaceSelection(selection, preparedBody),
          subject: previous.subject,
        },
        true
      );
      return;
    }

    if (mode === 'insertBelow' && !canApplyInsertBelow) {
      return;
    }

    const applied = buildAppliedDraft(
      previous,
      preparedBody,
      activeProposal.draft.subject,
      mode,
      isHtml,
      canReplaceSubject(previous)
    );

    if (
      !isPlaceholderSafeDraft(
        placeholderBaseline(activeProposal, mode, previous),
        applied,
        allowedLiterals
      )
    ) {
      return;
    }

    commitApplied(activeProposal, previous, applied, true);
  };

  const handleDiscard = () => {
    abortPendingRequest();
    setProposal(null);
    setActiveSources(null);
    setErrorKind(null);
    setIsPromptOpen(true);
  };

  const handlePromptClose = () => {
    if (!hasBarContent) {
      handleClose();
      return;
    }

    setIsPromptOpen(false);
    setActiveSources(null);
    setErrorKind(null);
    editorRef.current?.focus();
  };

  // Each Undo steps back over one accepted proposal: after "Shorter" and "More formal", two Undos
  // return to the draft before both.
  const handleUndo = () => {
    if (!canUndo || isNil(appliedStep)) {
      return;
    }

    onApplyRef.current(appliedStep.previous);
    setAppliedSteps((steps) => steps.slice(0, -1));
    setActiveSources(null);
    setIsChangeHighlighted(false);
    editorRef.current?.focus();
  };

  const handleStop = () => {
    abortPendingRequest();
    setIsPromptOpen(true);
  };

  const handleEscape = () =>
    match({
      hasSources: isNotNil(activeSources),
      isPromptOpen,
      isLoading,
      hasResult: isNotNil(proposal),
    })
      .with({hasSources: true}, () => {
        setActiveSources(null);
        // The source content lives in a portal that disappears when dismissed.
        editorRef.current?.focus();
      })
      .with({isPromptOpen: true}, handlePromptClose)
      // The request is already on its way; Escape out of habit must not throw its answer away.
      // Cancel in the footer bar stops it.
      .with({isLoading: true}, () => undefined)
      .with({hasResult: true}, handleDiscard)
      .otherwise(handleClose);

  const entryTitle = i18n.t('general.aiMessageComposer.labels.title');
  const isEntryDisabled = (params.isDisabled ?? false) || (!canGenerate && !hasExistingBody);

  return {
    channel: params.channel,
    isEnabled,
    isOpen: isEnabled && isOpen,
    isPromptOpen: isEnabled && isOpen && isPromptOpen,
    hasBarContent,
    isPending: isLoading,
    isError,
    errorKind,
    isChangeHighlighted,
    isProposalWithdrawn,
    canGenerate,
    canRunGenerate: canGenerate && instruction.trim() !== '',
    canUndo,
    canApplyReplace,
    canApplyInsertBelow,
    isInsertBelowOffered,
    isProposedSubjectApplied,
    canRefineDetail,
    canRefineSelection: isNotNil(params.editor),
    hasExistingBody,
    hasSubject: params.channel === 'email',
    isHtmlBody: isHtml,
    entryTitle,
    isEntryDisabled,
    instruction,
    selectionInstruction,
    suggestions: getAiComposerSuggestions({
      context: recordContext.context,
      translate: (key) => i18n.t(key),
      now: openedAt,
      formatValue: (fact) =>
        formatAiComposerFactValue(fact, {
          formatCurrency,
          formatNumber,
          formatDate: (value) => formatDateTime('dateShort', value),
          formatDateTime: (value) => formatDateTime('dateTimeShort', value),
          parseDate,
        }),
    }),
    recordContext: recordContext.context,
    isRecordContextLoading: recordContext.isLoading,
    isRecordContextError: recordContext.isError,
    proposal: activeProposal,
    undoStep: isAppliedIdentityIntact ? appliedStep : null,
    activeSources,
    onSourcesChange: setActiveSources,
    appliedProvenance: isAppliedDraftIntact ? (appliedStep?.provenance ?? null) : null,
    labelAction: isEnabled
      ? {
          title: entryTitle,
          leftIcon: 'action/magic_wand',
          overflowWrap: 'anywhere',
          isDisabled: isEntryDisabled,
          onClick: handleOpen,
        }
      : null,
    onOpen: handleOpen,
    onClose: handleClose,
    onPromptClose: handlePromptClose,
    onInstructionChange: (value) => setInstruction(value ?? ''),
    onSelectionInstructionChange: (value) => setSelectionInstruction(value ?? ''),
    onGenerate: () => runRequest('generate'),
    onGenerateSuggestion: (suggestionInstruction) => {
      setInstruction(suggestionInstruction);
      runRequest('generate', undefined, suggestionInstruction);
    },
    // A leftover instruction ("add a sentence about financing") must not ride along with
    // "Shorter" or "More formal"; those act on the text as it is.
    onRefine: (refinement) => runRequest('refine', refinement, ''),
    onRefineWithInstruction: (refinement) => runRequest('refine', refinement),
    onRefineSelection: () =>
      runRequest('refine', 'selection', selectionInstruction, 'selectionToolbar'),
    onStop: handleStop,
    onApply: handleApply,
    onDiscard: handleDiscard,
    onUndo: handleUndo,
    onEscape: handleEscape,
    onEditorKeyDownCapture: (event) => {
      if (!isEnabled || !isOpen || event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      handleEscape();
    },
  };
}
